import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  Alert,
  LinearProgress,
  Grid,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SpeedIcon from '@mui/icons-material/Speed';
import WarningIcon from '@mui/icons-material/Warning';
import { mockCostEstimates } from './mockData';

const CreditEstimatorMock = ({ 
  websiteCount = 500, 
  enrichmentColumns = [], 
  userCredits = 10000,
  showDetailedBreakdown = true 
}) => {
  
  const calculations = useMemo(() => {
    const baseAnalysis = {
      credits: websiteCount * mockCostEstimates.baseAnalysis.creditsPerWebsite,
      cost: websiteCount * mockCostEstimates.baseAnalysis.costPerWebsite,
    };

    const enrichmentAnalysis = enrichmentColumns.reduce((acc, column) => {
      const creditsPerSite = column.useWebSearch 
        ? mockCostEstimates.enrichment.webSearch.creditsPerColumn
        : mockCostEstimates.enrichment.contentExtraction.creditsPerColumn;
      
      const costPerSite = column.useWebSearch
        ? mockCostEstimates.enrichment.webSearch.costPerColumn
        : mockCostEstimates.enrichment.contentExtraction.costPerColumn;

      return {
        credits: acc.credits + (websiteCount * creditsPerSite),
        cost: acc.cost + (websiteCount * costPerSite),
      };
    }, { credits: 0, cost: 0 });

    const totalCredits = baseAnalysis.credits + enrichmentAnalysis.credits;
    const totalCost = baseAnalysis.cost + enrichmentAnalysis.cost;

    // Processing time estimation
    const baseTime = websiteCount * mockCostEstimates.processingTime.baseTimePerWebsite;
    const enrichmentTime = enrichmentColumns.reduce((acc, col) => {
      return acc + (websiteCount * mockCostEstimates.processingTime.enrichmentTimePerColumn);
    }, 0);
    const totalTimeSeconds = baseTime + enrichmentTime;
    const estimatedMinutes = Math.ceil(totalTimeSeconds / mockCostEstimates.processingTime.maxConcurrency / 60);

    return {
      baseAnalysis,
      enrichmentAnalysis,
      totalCredits,
      totalCost,
      estimatedMinutes,
      remainingCredits: userCredits - totalCredits,
      isAffordable: totalCredits <= userCredits,
    };
  }, [websiteCount, enrichmentColumns, userCredits]);

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main">
                {calculations.totalCredits.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Credits
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {formatCurrency(calculations.totalCost)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estimated Cost
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {formatTime(calculations.estimatedMinutes)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Processing Time
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                color={calculations.isAffordable ? "success.main" : "error.main"}
              >
                {calculations.remainingCredits.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Remaining Credits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Credit Balance Warning */}
      {!calculations.isAffordable && (
        <Alert severity="error" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <Typography variant="body2">
            <strong>Insufficient Credits:</strong> You need {calculations.totalCredits.toLocaleString()} credits 
            but only have {userCredits.toLocaleString()} available. 
            You're short by {(calculations.totalCredits - userCredits).toLocaleString()} credits.
          </Typography>
        </Alert>
      )}

      {showDetailedBreakdown && (
        <>
          {/* Detailed Breakdown */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachMoneyIcon sx={{ mr: 1 }} />
                Cost Breakdown
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="center">Rate</TableCell>
                      <TableCell align="center">Credits</TableCell>
                      <TableCell align="center">Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Base Analysis */}
                    <TableRow>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            Base Website Analysis
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Qualification analysis using GPT-4o
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">{websiteCount} websites</TableCell>
                      <TableCell align="center">1 credit/site</TableCell>
                      <TableCell align="center">
                        <Chip label={calculations.baseAnalysis.credits} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        {formatCurrency(calculations.baseAnalysis.cost)}
                      </TableCell>
                    </TableRow>

                    {/* Enrichment Columns */}
                    {enrichmentColumns.map((column, index) => {
                      const creditsPerSite = column.useWebSearch ? 3 : 1;
                      const costPerSite = column.useWebSearch ? 0.0105 : 0.0035;
                      const totalCredits = websiteCount * creditsPerSite;
                      const totalCost = websiteCount * costPerSite;

                      return (
                        <TableRow key={column.id || index}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {column.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {column.useWebSearch ? 'Web search enrichment' : 'Content extraction'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">{websiteCount} websites</TableCell>
                          <TableCell align="center">{creditsPerSite} credit{creditsPerSite > 1 ? 's' : ''}/site</TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={totalCredits} 
                              size="small" 
                              color={column.useWebSearch ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {formatCurrency(totalCost)}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Total Row */}
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body1" fontWeight="bold">
                          Total
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={calculations.totalCredits} 
                          size="small" 
                          color="primary"
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(calculations.totalCost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Processing Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <SpeedIcon sx={{ mr: 1 }} />
                Processing Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <AccessTimeIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6">
                      {formatTime(calculations.estimatedMinutes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Estimated Duration
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {mockCostEstimates.processingTime.maxConcurrency}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Parallel Processing
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      websites at once
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      ~{Math.round(websiteCount / calculations.estimatedMinutes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Throughput
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      websites per minute
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                Processing time may vary based on website response times and content complexity. 
                You can pause and resume the job at any time.
              </Alert>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default CreditEstimatorMock;